/** @format */
import React from 'react'
import PropTypes from 'prop-types'

import cs from 'classnames'
import Moment from 'moment'
import { extendMoment } from 'moment-range'
import { InputField } from '@/ui/Input'
import ClickAwayListener from '@/ui/ClickAwayListener'

import DaysView from './day-view'
import MonthsView from './month-view'
import YearsView from './year-view'
import Util from './util'

const moment = extendMoment(Moment)

class Calendar extends React.Component {
  static propTypes = {
    closeOnSelect: PropTypes.bool,
    computableFormat: PropTypes.string,
    strictDateParsing: PropTypes.bool,
    parsingFormat: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    date: PropTypes.any,
    minDate: PropTypes.any,
    maxDate: PropTypes.any,
    format: PropTypes.string,
    inputName: PropTypes.string,
    inputFieldId: PropTypes.string,
    inputFieldClass: PropTypes.string,
    labelText: PropTypes.string,
    minView: PropTypes.number,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    openOnInputFocus: PropTypes.bool,
    placeholder: PropTypes.string,
    hideTouchKeyboard: PropTypes.bool,
    todayText: PropTypes.string,
    disabled: PropTypes.bool,
  }

  static defaultProps = {
    date: null,
    closeOnSelect: false,
    computableFormat: 'YYYY-MM-DD HH:mm:ss',
    format: 'MM-DD-YYYY',
    openOnInputFocus: true,
    strictDateParsing: false,
    minDate: null,
    maxDate: null,
    minView: 0,
  }

  state = {
    date: this.props.date
      ? this.props.parsingFormat
        ? moment(this.props.date, this.props.parsingFormat, true)
        : moment(this.props.date)
      : null,
    inputValue: this.props.date
      ? moment(Util.toDate(this.props.date)).format(this.props.format)
      : undefined,
    currentView: this.props.minView | 0, // Convert any value to a integer
    isVisible: false,
  }

  componentWillReceiveProps(nextProps) {
    const newState = {
      date: nextProps.date
        ? this.props.parsingFormat
          ? moment(nextProps.date, nextProps.parsingFormat, true)
          : moment(nextProps.date)
        : this.state.date,
      inputValue: nextProps.date
        ? moment(Util.toDate(nextProps.date)).format(this.props.format)
        : null,
      minDate: nextProps.minDate ? moment(Util.toDate(nextProps.minDate)) : null,
      maxDate: nextProps.maxDate ? moment(Util.toDate(nextProps.maxDate)) : null,
    }
    if (nextProps.disabled === true) {
      newState.isVisible = false
    }

    this.setState(newState)
  }

  changeDate = e => {
    this.setState({ inputValue: e.target.value })
  }

  checkIfDateDisabled(date) {
    return (
      (date && this.props.minDate && date.isBefore(this.props.minDate, 'day')) ||
      (date && this.props.maxDate && date.isAfter(this.props.maxDate, 'day'))
    )
  }

  clickAway = e => {
    this.setVisibility(false)
    this.setState({ isCalendar: false })
  }

  inputBlur = e => {
    let newDate = null
    let computableDate = null
    const date = this.state.inputValue

    // Date is already set
    if (date) {
      // format, with strict parsing true, so we catch bad dates
      newDate = moment(date, this.props.format, true)
      // if the new date didn't match our format, see if the native
      // js date can parse it
      if (!newDate.isValid() && !this.props.strictDateParsing) {
        let d = new Date(date)
        // if native js cannot parse, just make a new date
        if (isNaN(d.getTime())) {
          d = new Date()
        }
        newDate = moment(d)
      }

      computableDate = newDate.format(this.props.computableFormat)
    }
    this.setState({
      date: newDate,
      inputValue: newDate ? newDate.format(this.props.format) : null,
    })

    if (this.props.onChange) {
      this.props.onChange(computableDate)
    }

    if (this.props.onBlur) {
      this.props.onBlur(e, computableDate)
    }
  }

  inputClick = e => {
    if (this.props.openOnInputFocus) {
      if (!this.state.isVisible) {
        this.toggleClick()
      }
    }
  }

  inputFocus = e => {
    if (this.props.openOnInputFocus) {
      if (!this.state.isVisible) {
        this.toggleClick()
      }
    }

    if (this.props.onFocus) {
      this.props.onFocus(e)
    }
  }

  keyDown = e => {
    Util.keyDownActions.call(this, e.keyCode)
  }

  nextView = () => {
    if (this.checkIfDateDisabled(this.state.date)) return
    const cv = this.state.currentView + 1
    this.setState({ currentView: cv })
  }

  prevView = date => {
    let newDate = date
    if (this.props.minDate && date.isBefore(this.props.minDate, 'day')) {
      newDate = this.state.minDate.clone()
    }

    if (this.props.maxDate && date.isAfter(this.props.maxDate, 'day')) {
      newDate = this.props.maxDate.clone()
    }

    if (this.state.currentView === this.props.minView) {
      this.setState({
        date: newDate,
        inputValue: date.format(this.props.format),
        isVisible: false,
      })
      if (this.props.onChange) {
        this.props.onChange(date.format(this.props.computableFormat))
      }
    } else {
      const cv = this.state.currentView - 1
      this.setState({
        date,
        currentView: cv,
      })
    }
  }

  setDate = (date, isDayView = false) => {
    if (this.checkIfDateDisabled(date)) return

    this.setState({
      date,
      inputValue: date.format(this.props.format),
      isVisible:
        this.props.closeOnSelect && isDayView ? !this.state.isVisible : this.state.isVisible,
    })

    if (this.props.onChange) {
      this.props.onChange(date.format(this.props.computableFormat))
    }
  }

  setVisibility(val) {
    const value = val !== undefined ? val : !this.state.isVisible
    const eventMethod = value ? 'addEventListener' : 'removeEventListener'

    document[eventMethod]('keydown', this.keyDown)

    if (this.state.isVisible !== value && !this.props.disabled) {
      this.setState({ isVisible: value })
    }
  }

  calendarClick = e => {
    e.stopPropagation()
    this.setState({ isCalendar: true })
  }

  calendarMouseDown = e => {
    // Prevent stealing focus from the input
    e.preventDefault()
  }

  todayClick = () => {
    const today = moment().startOf('day')

    if (this.checkIfDateDisabled(today)) return

    this.setState({
      date: today,
      inputValue: today.format(this.props.format),
      currentView: this.props.minView,
    })

    if (this.props.onChange) {
      this.props.onChange(today.format(this.props.computableFormat))
    }
  }

  toggleClick = () => {
    this.setState({ isCalendar: true })
    this.setVisibility()
  }

  render() {
    const {
      hideTouchKeyboard,
      todayText,
      disabled,
      theme,
      errorText,
      // other props
    } = this.props

    // its ok for this.state.date to be null, but we should never
    // pass null for the date into the calendar pop up, as we want
    // it to just start on todays date if there is no date set
    const calendarDate = this.state.date || moment()
    const todayLabel = todayText || (moment.locale() === 'de' ? 'Heute' : 'Today')
    const calendarClass = cs({ 'input-calendar-wrapper': true })
    const displayed = !(!this.state.isVisible || disabled)

    let readOnly = false

    if (hideTouchKeyboard) {
      // do not break server side rendering:
      try {
        if (
          /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
        ) {
          readOnly = true
        }
      } catch (e) {
        console.warn(e)
      }
    }

    return (
      <ClickAwayListener onClickAway={this.clickAway}>
        <div className="input-calendar">
          <InputField
            className={cs({ [theme['focus']]: displayed })}
            name={this.props.inputName}
            id={this.props.inputFieldId}
            labelText={this.props.labelText}
            onBlur={this.inputBlur}
            onChange={this.changeDate}
            onFocus={this.inputFocus}
            onClick={this.inputClick}
            placeholder={this.props.placeholder}
            readOnly={readOnly}
            disabled={this.props.disabled}
            type="text"
            value={this.state.inputValue || ''}
            errorText={errorText}
          />
          <If condition={displayed}>
            <div
              className={calendarClass}
              onClick={this.calendarClick}
              onMouseDown={this.calendarMouseDown}
            >
              <Choose>
                <When condition={this.state.currentView === 0}>
                  <DaysView
                    date={calendarDate}
                    nextView={this.nextView}
                    maxDate={this.props.maxDate}
                    minDate={this.props.minDate}
                    setDate={this.setDate}
                  />
                </When>
                <When condition={this.state.currentView === 1}>
                  <MonthsView
                    date={calendarDate}
                    nextView={this.nextView}
                    maxDate={this.props.maxDate}
                    minDate={this.props.minDate}
                    prevView={this.prevView}
                    setDate={this.setDate}
                  />
                </When>
                <When condition={this.state.currentView === 2}>
                  <YearsView
                    date={calendarDate}
                    maxDate={this.props.maxDate}
                    minDate={this.props.minDate}
                    prevView={this.prevView}
                    setDate={this.setDate}
                  />
                </When>
                <Otherwise>
                  <DaysView
                    date={calendarDate}
                    nextView={this.nextView}
                    maxDate={this.props.maxDate}
                    minDate={this.props.minDate}
                    setDate={this.setDate}
                  />
                </Otherwise>
              </Choose>
              <span
                className={`today-btn${this.checkIfDateDisabled(moment().startOf('day'))
                  ? ' disabled'
                  : ''}`}
                onClick={this.todayClick}
              >
                {todayLabel}
              </span>
            </div>
          </If>
        </div>
      </ClickAwayListener>
    )
  }
}

export default Calendar



// WEBPACK FOOTER //
// ./ui/DatePicker/DatePicker.js